import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  InputBase,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import { useMediaQuery } from "@mui/material";
import CustomPagination from "./CustomPagination";
import CloseIcon from "@mui/icons-material/Close";
import { get_email_logs } from "../Api service/APIvariables";

const Status = () => {
  const [currentTab, setCurrentTab] = useState("sent");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [tabIndex, setTabIndex] = useState(0); // To track the active tab
  const [searchQuery, setSearchQuery] = useState("");
  const isMobile = useMediaQuery("(max-width: 768px)");

  // State for Dialog modal
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  // Refs to hold tab elements for dynamic sizing
  const tabRefs = useRef([]);
  const [tabStyles, setTabStyles] = useState({ width: 0, left: 0 });

  // Update the sliding background when the tabIndex changes
  useEffect(() => {
    const currentTab = tabRefs.current[tabIndex];
    if (currentTab) {
      const { offsetWidth, offsetLeft, width } = currentTab;

      setTabStyles({
        width: offsetWidth,
        left: offsetLeft,
      });
    }
  }, [tabIndex, isMobile]);

  const handleTabChange = (newIndex) => {
    setTabIndex(newIndex);
    setCurrentTab(
      newIndex === 0 ? "sent" : newIndex === 1 ? "failed" : "responded"
    );
  };

  const [emailStatusData, setEmailStatusData] = useState({
    sent: [],
    failed: [],
    responded: [],
  });

  useEffect(() => {
    const fetchEmailLogs = async () => {
      try {
        const user_id = localStorage.getItem("user_id");
        const token = localStorage.getItem("auth_token");
        if (!user_id || !token) return;

        const formData = new FormData();
        formData.append("user_id", user_id);

        const response = await fetch(get_email_logs, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        const result = await response.json();
        if (result.success !== false && result.data) {
          const logs = result.data;
          const sent = logs.filter((log) => log.status === "sent");
          const failed = logs.filter((log) => log.status === "failed");
          
          setEmailStatusData({
            sent,
            failed,
            responded: [], // Keeping responded empty for now as requested
          });
        }
      } catch (error) {
        console.error("Error fetching email logs:", error);
      }
    };

    fetchEmailLogs();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filterEmails = (emails) => {
    if (!searchQuery) return emails;
    return emails.filter(
      (email) =>
        email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        email.recipient.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const handleEmailClick = (email) => {
    setSelectedEmail(email);
    setOpenDialog(true); // Open dialog on email click
  };

  const renderEmails = (emails) => {
    const filteredEmails = filterEmails(emails);
    const paginatedEmails = filteredEmails.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
    
    return paginatedEmails.length > 0 ? (
      <List sx={{ padding: 0 }}>
        {paginatedEmails.map((email) => (
          <ListItem
            key={email.id}
            sx={{
              borderBottom: "1px solid #e0e0e0",
              ":hover": {
                backgroundColor: "#f9f9f9",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              },
              transition: "background-color 0.3s, box-shadow 0.3s",
            }}
            onClick={() => handleEmailClick(email)} // Open dialog when email is clicked
          >
            <ListItemText
              primary={email.subject}
              secondary={`Recipient: ${email.recipient}`}
            />
          </ListItem>
        ))}
      </List>
    ) : (
      <Typography
        color="textSecondary"
        sx={{ textAlign: "center", fontStyle: "italic" }}
      >
        No emails match your search.
      </Typography>
    );
  };

  return (
    <div
      style={{
        display: "block",
        minHeight: "100vh",
        position: "relative",
        width: "100%",
        margin: "0 auto",
        paddingLeft: "20px",
        paddingRight: isMobile ? "15px" : "0px",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#eceff1",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          marginBottom: "20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#eceff1",
            borderRadius: "4px",
            width: { xs: "120px", sm: "200px" },
            padding: "2px 10px",
            border: "1px solid #CFD8DC",
            justifyContent: "space-between",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              borderColor: "#1976d2",
              boxShadow: "0 4px 8px rgba(25, 118, 210, 0.2)",
            },
            "&:focus-within": {
              boxShadow: "0 4px 8px rgba(25, 118, 210, 0.2)",
              borderColor: "#1976d2",
            },
          }}
        >
          <InputBase
            sx={{
              flex: 1,
              color: "#212121",
              fontSize: "14px",
            }}
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          maxWidth: "1200px",
          width: { xs: "100%", sm: "90%", md: "100%" },
          padding: "30px",
          borderRadius: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#eceff1",
            width: isMobile ? "100%" : "fit-content", // Adjusted width to fit content dynamically
            maxWidth: "500px", // Prevents it from stretching too much
            borderRadius: "8px",
            padding: "10px", // Increased horizontal padding for balance
            margin: "0 auto 20px", // Centers it and keeps margin at the bottom
            position: "relative",
          }}
        >
          {/* Sliding background */}
          <div
            style={{
              // display: "flex",
              content: "''",
              position: "absolute",
              padding: 4,
              top: "10%",
              left: `${tabStyles.left}px`,
              width: `${tabStyles.width}px`,
              // minWidth: "50px",
              height: "80%",
              backgroundColor: "#453c72",
              borderRadius: "6px",
              transition: "left 0.3s ease, width 0.3s ease",
              zIndex: 1,
              flexShrink: 0,
            }}
          ></div>

          {/* Tabs */}
          <div
            style={{
              display: "flex",
              gap: isMobile ? 0 : "10px",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 2, // Ensure tabs are above the background
            }}
          >
            {["Sent", "Failed", "Responded"].map((tab, index) => (
              <div
                key={tab}
                ref={(el) => (tabRefs.current[index] = el)}
                onClick={() => handleTabChange(index)}
                style={{
                  cursor: "pointer",
                  // backgroundColor:
                  //   tabIndex === index ? "#453c72" : "transparent",
                  color: tabIndex === index ? "white" : "black",
                  zIndex: 2,
                  minWidth: "100px",
                  textAlign: "center",
                }}
              >
                {tab}
              </div>
            ))}
          </div>
        </div>

        {/* Render emails based on the active tab */}
        <div>{renderEmails(emailStatusData[currentTab])}</div>
      </Paper>

      <div
        style={{
          display: "flex",
          width: isMobile ? "100%" : "40%",
          justifyContent: "flex-end",
          alignItems: "center",
          marginLeft: "auto",
          marginTop: 0,
        }}
      >
        <CustomPagination
          count={filterEmails(emailStatusData[currentTab])?.length}
          rowsPerPage={rowsPerPage}
          page={page} // Use `page` instead of `currentPage`
          onPageChange={(event, newPage) => handleChangePage(event, newPage)} // Use `handleChangePage` here
          onRowsPerPageChange={handleChangeRowsPerPage} // Use `handleChangeRowsPerPage` here
        />
      </div>

      {/* Dialog Modal */}
      <Dialog open={openDialog} onClose={handleDialogClose}>
        {/* <DialogTitle>Email Details</DialogTitle> */}
        <DialogActions>
          <IconButton
            onClick={handleDialogClose}
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogActions>

        <DialogContent>
          {selectedEmail && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <div style={{ height: "50px" }}>
                <Typography variant="h6">{selectedEmail.subject}</Typography>
              </div>
              <div
                style={{
                  height: "70px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body1">
                  <span style={{ fontWeight: 600 }}>MDQ</span>{" "}
                  {`<${selectedEmail.recipient}>`}
                </Typography>
                <span>{selectedEmail.created_at ? new Date(selectedEmail.created_at).toLocaleString() : ""}</span>
              </div>
              <div style={{ minHeight: "300px" }}>
                <textarea
                  variant="body1"
                  value={selectedEmail.message}
                  // aria-expanded={false}
                  style={{ width: "100%", minHeight: "250px", resize: "none" }}
                />
              </div>
            </Box>
          )}
        </DialogContent>
        {/* <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Close
          </Button>
        </DialogActions> */}
      </Dialog>
    </div>
  );
};

export default Status;
